/**
 * "Where is it?" — the Map View's location search.
 *
 * Placing a camera used to mean dragging the world into position by hand: open the board on
 * the default view, pan, zoom, pan again, and hope you recognised the right junction. This
 * takes the query instead — a place name, a coordinate pair, or a URL pasted out of whatever
 * map application is already open — and moves the view there.
 *
 * TWO KINDS OF QUERY, AND ONLY ONE NEEDS THE NETWORK
 * ─────────────────────────────────────────────────
 * Coordinates are recognised as you type and resolved in the browser (see geocode.ts), so
 * they work on a node with no route out — the same node that has no basemap either, and
 * still has pins worth placing. Place names need the configured geocoder, so they only run
 * on an explicit submit and say plainly when that service cannot be reached. The difference
 * is surfaced rather than hidden: an operator who cannot search by name can still be told
 * "coordinates still work".
 *
 * WHY SUBMIT AND NOT SEARCH-AS-YOU-TYPE
 * ────────────────────────────────────
 * The default geocoder is public Nominatim, whose terms are one request a second and no
 * per-keystroke querying. Waiting for Enter is not a limitation to work around: it is the
 * behaviour that keeps every deployment a good citizen of a service nobody here pays for.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  GeocoderUnreachable,
  type GeoSearchResult,
  formatCoordinates,
  geocode,
  parseCoordinates,
} from './geocode';

export interface MapSearchBoxProps {
  /** Move the map. `zoom` is omitted when the caller should keep the current one. */
  onGo: (target: { lat: number; lng: number; zoom?: number }) => void;
  /** Frame a result that came with one — a city should fit, not land at street level. */
  onFit?: (bbox: [number, number, number, number]) => void;
  /** From `geo_map_config`; undefined uses the built-in public default. */
  geocoderUrl?: string;
  /** Hint shown under the box while the operator is placing cameras. */
  editing?: boolean;
  className?: string;
}

/**
 * Where a name-less coordinate lands. Close enough to read a building, wide enough that a
 * coordinate a few metres off still shows its surroundings — an operator placing a camera is
 * matching what they see to what they remember, and a rooftop fills the screen at 19.
 */
const COORDINATE_ZOOM = 17;

type Status =
  | { kind: 'idle' }
  | { kind: 'searching' }
  | { kind: 'results'; rows: GeoSearchResult[] }
  | { kind: 'empty' }
  | { kind: 'error'; message: string };

export default function MapSearchBox({
  onGo,
  onFit,
  geocoderUrl,
  editing = false,
  className = '',
}: MapSearchBoxProps) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [open, setOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Recognised as you type, resolved with no network — see the file header.
  const coords = parseCoordinates(query);

  // In flight when the operator navigates away or searches again: the late answer would
  // otherwise replace the new one.
  useEffect(() => () => abortRef.current?.abort(), []);

  // A click anywhere else means they are done with the list. The map is the point of the
  // page and a dropdown sitting over it is in the way.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const goTo = useCallback(
    (row: GeoSearchResult) => {
      if (row.bbox && onFit) onFit(row.bbox);
      else onGo({ lat: row.lat, lng: row.lng, zoom: COORDINATE_ZOOM });
      setOpen(false);
    },
    [onFit, onGo],
  );

  const submit = useCallback(async () => {
    const text = query.trim();
    if (!text) return;

    // Coordinates never touch the network, so they answer instantly and cannot fail.
    if (coords) {
      onGo({ lat: coords.lat, lng: coords.lng, zoom: coords.zoom ?? COORDINATE_ZOOM });
      setOpen(false);
      setStatus({ kind: 'idle' });
      return;
    }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus({ kind: 'searching' });
    setOpen(true);
    try {
      const rows = await geocode(text, { url: geocoderUrl, signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      setStatus(rows.length ? { kind: 'results', rows } : { kind: 'empty' });
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
      setStatus({
        kind: 'error',
        message: e instanceof GeocoderUnreachable || e?.name === 'GeocoderUnreachable'
          ? e.message
          : 'Place search failed',
      });
    }
  }, [coords, geocoderUrl, onGo, query]);

  const clear = () => {
    abortRef.current?.abort();
    setQuery('');
    setStatus({ kind: 'idle' });
    setOpen(false);
  };

  return (
    <div ref={rootRef} data-tour="map-place-search" className={`w-[17rem] max-w-[calc(100vw-5rem)] sm:w-[21rem] ${className}`}>
      <div className="flex items-center gap-1 rounded-md border border-border bg-card/95 px-2 py-1 shadow-card backdrop-blur-sm">
        <MagnifyingGlassIcon className="h-3.5 w-3.5 flex-none text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (status.kind !== 'idle') setStatus({ kind: 'idle' });
            setOpen(!!parseCoordinates(e.target.value));
          }}
          onFocus={() => setOpen(!!coords || status.kind === 'results')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            } else if (e.key === 'Escape') {
              clear();
            }
          }}
          placeholder="Search a place, or paste 19.076, 72.878"
          aria-label="Search for a location on the map"
          className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="flex-none rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!query.trim()}
          className="flex-none rounded bg-brand px-2 py-0.5 text-[11px] font-medium text-white disabled:opacity-40"
        >
          Go
        </button>
      </div>

      {open && (
        <div className="mt-1 overflow-hidden rounded-md border border-border bg-card/98 shadow-card backdrop-blur-sm">
          {/* Coordinates: shown as a confirmable row rather than jumping mid-typing, because
              "19.07" is a prefix of "19.076" and moving the map on every keystroke would
              drag the operator across the country while they are still typing. */}
          {coords ? (
            <button
              type="button"
              onClick={() => void submit()}
              className="flex w-full items-start gap-2 px-2.5 py-2 text-left hover:bg-muted"
            >
              <MapPinIcon className="mt-0.5 h-3.5 w-3.5 flex-none text-brand" />
              <span className="min-w-0">
                <span className="block text-xs text-foreground">
                  Go to {formatCoordinates(coords.lat, coords.lng)}
                </span>
                <span className="block text-[11px] text-muted-foreground">
                  {coords.swapped
                    // Said out loud: the pair was out of range as latitude, longitude, so it
                    // was read the other way round. An operator who meant the other place
                    // needs to see that something was decided for them.
                    ? 'read as longitude, latitude'
                    : 'coordinates — no network needed'}
                </span>
              </span>
            </button>
          ) : status.kind === 'searching' ? (
            <p className="px-2.5 py-2 text-[11px] text-muted-foreground">Searching…</p>
          ) : status.kind === 'results' ? (
            <ul className="max-h-56 overflow-y-auto">
              {status.rows.map((row, i) => (
                <li key={`${row.lat},${row.lng},${i}`}>
                  <button
                    type="button"
                    onClick={() => goTo(row)}
                    className="flex w-full items-start gap-2 px-2.5 py-1.5 text-left hover:bg-muted"
                  >
                    <MapPinIcon className="mt-0.5 h-3.5 w-3.5 flex-none text-muted-foreground" />
                    <span className="min-w-0">
                      <span className="block truncate text-xs text-foreground">{row.label}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {formatCoordinates(row.lat, row.lng)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : status.kind === 'empty' ? (
            <p className="px-2.5 py-2 text-[11px] text-muted-foreground">
              Nothing found. Try a fuller address, or paste coordinates.
            </p>
          ) : status.kind === 'error' ? (
            <p className="px-2.5 py-2 text-[11px] text-muted-foreground">
              <span className="text-destructive">{status.message}.</span>{' '}
              {/* The honest fallback, and the reason coordinates are parsed locally. */}
              Coordinates still work — paste a lat, lng pair.
            </p>
          ) : null}
        </div>
      )}

      {editing && !open && (
        <p className="mt-1 px-1 text-[11px] text-muted-foreground">
          Find the location first, then pick a camera and click to place it.
        </p>
      )}
    </div>
  );
}
